import { useState, useEffect, useMemo } from 'react';
import TabPanel from '../tab-panel/tab-panel';
import TabsList from '../tabs-list/tabs-list';
import Table from '../table/table';
import ActionList from '../action-list/action-list';
import TableSummary from '../table-summary/table-summary';
import MainHead from '../main-head/main-head';
import PlanActions from '../planAction/PlanActions';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { getDataMain, getFetchingStatusMainData } from '../../store/data-main/selectors';
import { getDataSummary, getFetchingStatusMainSummary } from '../../store/data-summary/selectors';
import { getDirections } from '../../store/directions/selectors';
import { getCurrentDate } from '../../store/date/selectors';
import { fetchDataMainRequest, createQuestionnaireRequest, updateQuestionnaireRequest } from '../../store/data-main/data-main';
import { fetchDataSummaryRequest } from '../../store/data-summary/data-summary';
import { fetchDirectionsRequest } from '../../store/directions/directions';
import { fetchCurrentDateRequest } from '../../store/date/date';
import { UniversalObject } from '../../types/data';
import { Quarter } from '../../types/data';
import { isNotStartedQuestionnaire, filterByRange, getCurrentQuarterAndYear } from '../../utils/utils';
import AlertInformation from '../alert-information/alert-information';
import { PlanActionsContext } from '../../types/types';
import { selectCurrentUser } from "src/modules/users/selector";
import Loading from 'src/components/Loading';
import { useSearchParams } from 'react-router-dom';

const quarters: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];

function MainPanel() {
  const [value, setValue] = useState(0);

  const [isModalOpenСomplete, setIsModalOpenСomplete] = useState<boolean>(false);
  const [isModalOpenReturn, setIsModalOpenReturn] = useState<boolean>(false);

  const [group, setGroup] = useState<UniversalObject | null>(null);
  const [factory, setFactory] = useState<UniversalObject>({});
  const [gop, setGop] = useState<UniversalObject>({});
  const [currentDirection, setCurrentDirection] = useState<UniversalObject | null>(null);

  const [year, setYear] = useState<number | null>(null);
  const [quarterIndex, setQuarterIndex] = useState<number | null>(null);
  const [pendingDirectionId, setPendingDirectionId] = useState<number | null>(null);

  const quarter = quarterIndex !== null ? quarters[quarterIndex] : null;

  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const user = useAppSelector((state) => selectCurrentUser(state));

  // // Mock user
  // const user = {
  //   displayName: 'Kanashov Evgeny',
  //   infoVPO: {
  //     "user": true,
  //     "zone": false,
  //     "admin": false,
  //     "adminRegion": false,
  //     "directoryTitle": ["BOP", "VOP"],
  //     "factory": [
  //       {
  //         factoryID: 6,
  //         factoryTitle: "Клин",
  //       },
  //       {
  //         factoryID: 8,
  //         factoryTitle: "Солодовня Казань",
  //       },
  //     ],
  //   }
  // };

  const dataMain = useAppSelector(getDataMain);
  const loadingDataMain = useAppSelector(getFetchingStatusMainData);
  const dataSummary = useAppSelector(getDataSummary);
  const loadingDataSummary = useAppSelector(getFetchingStatusMainSummary)
  const directions = useAppSelector(getDirections);
  const currentDate = useAppSelector(getCurrentDate);

  const context: PlanActionsContext = {
    selectedGroup: group,
    selectedFactory: factory,
    selectedGop: gop,
    currentQuarter: quarter,
    currentYear: year,
    currentDirection: currentDirection
  };

  // Фильтруем направления по дате отображения
  const filteredDirections = useMemo(() => {
    if (year !== null && quarter !== null && directions.length > 0) {
      return filterByRange(directions, quarter, year);
    }
    return [];
  }, [directions, year, quarter]);

  const isDisableQuestionnaire = useMemo(() => {
    if (user.infoVPO.adminRegion || user.infoVPO.zone) return;

    if (user.infoVPO.user || user.infoVPO.admin) {
      return user.infoVPO.factory.every((item) => item.factoryTitle !== factory.name);
    }
  }, [user, factory]);

  // Устанавливаем текущий квартал и год после запроса
  useEffect(() => {
    if (currentDate) {
      const date = getCurrentQuarterAndYear(currentDate);
      setYear(date?.year);
      const idx = quarters.findIndex((quarter) => quarter === date?.quarter);
      if (idx !== -1) {
        setQuarterIndex(idx);
      }
    }
  }, [currentDate]);

  useEffect(() => {
    if (gop?.id) {
      dispatch(fetchDirectionsRequest({ gop: gop.id }));
    }
  }, [gop, dispatch]);

  // Устанавливаем при первом рендере текущее направление
  useEffect(() => {
    if (directions && filteredDirections.length > 0 && directions.length > 0 && value === 0) {
      if (pendingDirectionId) {
        const targetDirection = filteredDirections.find((d: any) => d.id === pendingDirectionId);
        if (targetDirection) {
          setCurrentDirection(targetDirection);
          setValue(targetDirection.index ?? 0);
          setPendingDirectionId(null);
          return;
        }
      }

      const actionCardId = searchParams.get('actionCardGOP');
      if (actionCardId) {
        return;
      }
      setCurrentDirection(filteredDirections[0]);
      setValue(filteredDirections[0].index ?? 0);
    }
  }, [directions, pendingDirectionId]);

  useEffect(() => {
    dispatch(fetchCurrentDateRequest());
  }, [dispatch]);

  // Получаем данные для опросников
  useEffect(() => {
    if (currentDirection?.id && factory?.id && quarter !== null && year !== null) {
      dispatch(fetchDataMainRequest({
        factory: factory.id,
        quarter: quarter,
        year: year,
        direction: currentDirection.id,
      }));
    }
  }, [factory, quarter, year, currentDirection, dispatch]);

  // Получаем данные для сводки
  useEffect(() => {
    if (value === 1 && gop?.id && factory?.id && year !== null) {
      dispatch(fetchDataSummaryRequest({
        gop: gop.id,
        factory: factory.id,
        quarter: getCurrentQuarterAndYear(currentDate)?.quarter,
        year: year,
      }));
    }
  }, [value, gop, factory, year, dispatch]);

  const handleTabChange = (newValue: UniversalObject) => {
    setCurrentDirection(newValue);
    setValue(newValue.index as number);
  };

  // Устанавливаем статус 'В процессе' для не начатого опросника
  const handleCreateQuestionnaire = () => {
    if (dataMain?.statusQuestionnaire.title === 'Не начато') {
      dispatch(createQuestionnaireRequest({
        factory: factory.id,
        direction: currentDirection.id,
        quarter: quarter,
        year: year,
        statusQuestionnaire: 'В процессе'
      }))
    }
  }

  const handleOpenModalСomplete = () => {
    setIsModalOpenСomplete(true);
  }

  const handleCloseModalСomplete = () => {
    setIsModalOpenСomplete(false);
  }

  const handleOpenModalReturn = () => {
    setIsModalOpenReturn(true);
  }

  const handleCloseModalReturn = () => {
    setIsModalOpenReturn(false);
  }

  const handleСompleteQuestionnaire = () => {
    if (dataMain?.statusQuestionnaire.title === 'Не начато') {
      dispatch(createQuestionnaireRequest({
        factory: factory.id,
        direction: currentDirection.id,
        quarter: quarter,
        year: year,
        statusQuestionnaire: 'Завершено',
        completionDate: new Date().toISOString(),
        nameCompleted: user.displayName
      }))
    } else {
      dispatch(updateQuestionnaireRequest({
        id: dataMain?.statusQuestionnaire.id,
        statusQuestionnaire: 'Завершено',
        completionDate: new Date().toISOString(),
        nameCompleted: user.displayName
      }));
    }

    setIsModalOpenСomplete(false);
  }

  const handleReturnQuestionnaire = () => {
    dispatch(updateQuestionnaireRequest({
      id: dataMain?.statusQuestionnaire.id,
      statusQuestionnaire: 'В процессе',
      completionDate: new Date().toISOString(),
      nameCompleted: ''
    }));
    setIsModalOpenReturn(false);
  }

  if (year === null || quarterIndex === null || !user.displayName) {
    return <Loading />;
  }

  return (
    <div className={`${value !== 2 ? 'max-h-[100vh] overflow-y-hidden' : ''}`}>
      <MainHead
        group={group}
        factory={factory}
        gop={gop}
        quarter={quarter as Quarter}
        year={year}
        user={user}
        onGroupChange={setGroup}
        onFactoryChange={setFactory}
        onGopChange={setGop}
        setQuarterIndex={setQuarterIndex}
        setYear={setYear}
        setPendingDirectionId={setPendingDirectionId}
      />

      <div className="p-7">
        {currentDirection && <TabsList isFixed data={filteredDirections} value={currentDirection} onChange={handleTabChange} />}

        <TabPanel value={value} index={0}>
          {dataMain &&
            <>
              <ActionList
                year={year}
                setYear={setYear}
                quarterIndex={quarterIndex}
                setQuarterIndex={setQuarterIndex}
                statusQuestionnaire={dataMain.statusQuestionnaire.title}
                dataMain={dataMain.data}
                departments={dataMain.dataHead}
                factory={factory}
                gop={gop}
                direction={currentDirection}
                user={user}
                currentDate={currentDate}
                isNotEditQuestionnaire={isDisableQuestionnaire as boolean}
                onButtonComplete={handleOpenModalСomplete}
                onButtonReturn={handleOpenModalReturn}
              />

              {loadingDataMain
                ?
                <div className='grid place-items-center min-h-[25vh]'>
                  <Loading />
                </div>
                :
                <Table
                  data={dataMain.data}
                  dataHead={dataMain.dataHead}
                  factory={factory.id as number}
                  quarter={quarter as Quarter}
                  year={year}
                  group={group?.id as number}
                  direction={currentDirection?.id as number}
                  gopId={gop?.id as number}
                  onCreateQuestionnaire={handleCreateQuestionnaire}
                  isNotEditQuestionnaire={dataMain.statusQuestionnaire.title === "Завершено" || isDisableQuestionnaire}
                  context={context}
                />}
            </>
          }
        </TabPanel>

        <TabPanel value={value} index={1}>
          <ActionList
            group={group}
            factory={factory}
            gop={gop}
            custom
            year={year}
            setYear={setYear}
            currentDate={currentDate}
            quarterIndex={quarterIndex}
            setQuarterIndex={setQuarterIndex}
            dataSummary={dataSummary}
            onButtonComplete={handleOpenModalСomplete}
          />
          {loadingDataSummary
            ?
            <div className='grid place-items-center min-h-[25vh]'>
              <Loading />
            </div>
            :
            <TableSummary data={dataSummary} />}
        </TabPanel>

        <TabPanel value={value} index={2}>
          <PlanActions
            context={context}
          />
        </TabPanel>
      </div>

      <AlertInformation
        isOpen={isModalOpenСomplete}
        custom
        title="Подтвердите действие"
        text="Вы уверены, что хотите завершить оценку? Ответы нельзя будет изменить после завершения оценки."
        textConfirmButton="Да, завершить"
        onClose={handleCloseModalСomplete}
        onConfirm={handleСompleteQuestionnaire}
      />

      <AlertInformation
        isOpen={isModalOpenReturn}
        custom
        title="Подтвердите действие"
        text="Вы уверены, что хотите вернуть опросник в работу? Все сотрудники, у которых есть доступ к опроснику, смогут его изменить и завершить."
        textConfirmButton="Да, вернуть в работу"
        onClose={handleCloseModalReturn}
        onConfirm={handleReturnQuestionnaire}
      />
    </div>
  );
}

export default MainPanel;
